import { useMemo, useState, type ReactNode } from 'react';
import {
  lineItemHasWork,
  type DurationUnit,
  type LineItem,
  type LineItemTargetOption,
  type Metafield,
  type Task,
  formatMoney,
  type ExternalCost,
  type Invoice,
  type LineItemEconomics,
  type SaveLineItemMoneyRequest,
  type ProjectMember,
  billedPct,
} from '@forecastify/shared';
import { useDuration } from '../../duration';
import { DurationUnitPicker, useDurationIn } from '../../duration-input';
import {
  Badge,
  Button,
  DataList,
  Dialog,
  Icon,
  TextInput,
  type DataListRow,
  type MenuItem,
} from '@forecastify/ui';
import type { RuleOptions } from './rule-editor';
import { LineItemSyncCard, type SyncSettingsPatch } from './line-item-target';
import { useT } from '../../i18n';
import {
  CodeChip,
  DetailFrame,
  InlineTitle,
  MoreMenu,
  ProgressBlock,
  RailCard,
  RailDate,
  RailLink,
  RailMoney,
  RailPicker,
  RailRow,
  RailValue,
  SectionAction,
  SectionHeader,
} from './detail-chrome';
import { statusLabel as invoiceStatusLabel } from './invoice-list';
import { CollapsibleHistory } from '../history/collapsible-history';
import { MetafieldsCard } from './metafields-card';
import { Attachments } from '../attachments';
import { DOCUMENT_FILES } from '../file-kinds';
import { ScopedDropSurface } from '../scoped-drop';
import { LINE_ITEM_TYPE_KEY } from './line-item-types';
import { listLabels } from '../delivery/clients-screen';
import { initialsOf } from '../identity';
import { useAvatars } from '../use-avatar';
import { EXCLUDED, NOBODY, type LineItemTasks } from './use-line-item-tasks';
import { baseCurrencyOf } from '../base-currency';
import type { FinanceTipText } from './finance/finance-tip';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C25 · The line item detail BODY. One component; three frames wrap it.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `2fazis.LineItemDetail.html`, **re-transcribed** on `detail-chrome.tsx` alongside the contract —
 * see that file for why the first pass of both came out looking like something else.
 *
 * Its main column is **Feladatok** with a toolbar, a table and a pager; its rail is **Státusz** and
 * **Részletek**. The two cards between them in the mockup — *Pénzügy* and *Erőforrás-allokációk* —
 * plus *Számlák*, *Külső költségek*, *Készültség*, *Betervezett* and *Hatókör* are the largest
 * inert block in the prototype (44 of 94 keys; `docs/mockup/PHASE-2.md`), and P2-1 defers the lot.
 *
 * ⚠️⚠️ **`Forrás` SAYS *kézi* FOR EVERY ROW TODAY, AND THE COLUMN IS HERE ANYWAY.** C26 gives it a
 * second value — a task pulled in by a rule rather than named by a person — and the difference is
 * the one thing somebody looking at a billing discrepancy needs. A column that appeared with the
 * feature would make "by hand" look like the absence of a choice rather than a choice.
 *
 * ⚠️⚠️ **BOTH MODES ARE REAL AS OF C26.** *Manuális* is somebody naming a task; *Szabály alapú* is
 * a condition list the engine re-evaluates on every task write. The `Forrás` badge on each row says
 * which one brought that task in, and it is the thing somebody looking at a billing discrepancy
 * needs: a task that appeared without anybody choosing it looks like a bug until the column says
 * *szabály*.
 */

export interface LineItemDetailData {
  readonly item: LineItem;
  /**
   * 31 Oct 2026 · its tasks, paged on the server, with the picker's search and the exclusions
   * resolved by id — `use-line-item-tasks.ts`. The route used to hold every task of the project.
   */
  readonly taskList: LineItemTasks;
  /** The contract it hangs off, for the rail's link. */
  readonly contractName: string;
  readonly projectName: string;
  readonly projectColor: string;
  /** C26 · what the rule's value pickers offer. */
  readonly ruleOptions: RuleOptions;
  /** `0161` · what this line item may target: active instances and their staged projects. */
  readonly targetOptions: readonly LineItemTargetOption[];
  /** C30 · the workspace's `lineitem` metafields. Empty when none are defined. */
  readonly metafields: readonly Metafield[];
  readonly members: readonly { readonly id: string; readonly name: string }[];
  /**
   * OS-1 · the project's members, with their windows — who a time-and-material line item may name.
   * Read separately and non-fatally: empty only narrows the picker to whoever is already named.
   */
  readonly projectMembers: readonly ProjectMember[];
  /**
   * C55 · the finance block, read separately and NON-FATAL — `null` is *not loaded* and the two
   * cards simply do not appear. Its figures are a join over every worklog, every rate card period
   * and the currency matrix, which is why it is its own address rather than a field on the item.
   */
  readonly economics: LineItemEconomics | null;
  /** C56 · the costs attached to THIS line item. Empty is an ordinary answer and the usual one. */
  readonly externalCosts: readonly ExternalCost[];
  /** C57 · the invoices billed against THIS line item. Empty is the usual answer. */
  readonly invoices: readonly Invoice[];
  /**
   * ⚠️ **Whether the contract above it RECURS**, which is the only thing that makes a scope
   * meaningful: `retainer` work consumes a period's frame and `billable` work sits outside it, and
   * a milestone contract has no frame to be inside.
   */
  readonly recurring: boolean;
  /**
   * 21 Oct 2026 · its project has no client. The type picker then offers non-billable only — the
   * server refuses the others (`internal_project_billable`).
   */
  readonly internal: boolean;
  /**
   * IP-3 · every line item's name by id, for the triangle on a task another line item also wants.
   * A line item this does not hold (another project's) reads as *another line item*.
   */
  readonly lineItemNames: ReadonlyMap<string, string>;
  /** The workspace's currencies, so the money dialog can offer them. Empty is an ordinary answer. */
  readonly currencies: readonly {
    readonly code: string;
    readonly symbol: string;
    readonly rateToBase: string;
    readonly isDefault?: boolean;
  }[];
}

export interface LineItemDetailHandlers {
  readonly canManage: boolean;
  /** ⚠️ `contracts.line_item_pricing` — the type control exists only on that tier (P2-1). */
  readonly pricing: boolean;
  readonly onPatch: (patch: Record<string, unknown>) => void;
  /** Points a task at this line item, or clears it when `itemId` is null. */
  readonly onAssign: (task: Task, itemId: string | null) => unknown;
  readonly onOpenContract: () => void;
  readonly onOpenProject: () => void;
  readonly onOpenTask: (task: Task) => void;
  /** IP-3 · opens another line item — the one a shared task is also on. */
  readonly onOpenLineItem: (lineItemId: string) => void;
  /**
   * 5 Nov 2026 · the sync card's *Mentés* — the target, the mode and the rule that changed, in ONE
   * patch. The server re-evaluates in the same request.
   */
  readonly onSyncSettings: (patch: SyncSettingsPatch) => unknown;
  /** `0161` · *Szinkronizálás most* — pull the target across now. */
  readonly onSync: () => void;
  /** C55 · writes the line item's own money. Absent for a reader who may not manage contracts. */
  readonly onMoney?: (patch: SaveLineItemMoneyRequest) => void;
  /**
   * C57 · raises an invoice against this line item, with what is left to bill already filled in.
   * ⚠️ Absent for a reader who may not manage contracts — `invBill` in the mockup.
   */
  readonly onBill?: () => void;
  /**
   * Records an external cost spent on this line item, the link already set. ⚠️ Absent for a
   * reader who may not manage contracts — the same authority the project's Költségek tab asks for.
   */
  readonly onNewCost?: () => void;
  /** C57 · opens one of the invoices billed against it. */
  readonly onOpenInvoice?: (invoice: Invoice) => void;
  /** C26 · throws a rule-matched task back, or takes the exclusion off. */
  readonly onExclude: (taskId: string, excluded: boolean) => unknown;
  readonly onMetafields: (values: Record<string, unknown>) => void;
  /** Grows the detail to the full-page frame. Absent when it is already there. */
  readonly onExpand?: () => void;
  /**
   * ⚠️ **`0116` · the line item's own folder.** 0044 gave a CONTRACT attachments and stopped, so
   * an estimate or a client's sign-off for one line item landed in a list shared with its three
   * siblings. Resolves to a refusal code, or `null` when it landed — the contract's does the same.
   */
  readonly onAttach: (file: File) => Promise<string | null>;
}

export function LineItemDetail({
  data,
  handlers,
  wide,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
  readonly wide: boolean;
}) {
  const [dropped, setDropped] = useState(0);
  /* ⚠️ C55's predicate, not a comparison against `'license'` — a fourth work-free type would
     otherwise have to be found in every file that asks this question. */
  const hasWork = lineItemHasWork(data.item.type);

  return (
    /* ⚠️ The Jira gesture — see `scoped-drop.tsx`. A line item has no description and no thread,
       so there is one destination and the veil names it. */
    <ScopedDropSurface
      entityLabel={data.item.identifier}
      enabled={handlers.canManage}
      zoneLabels={{}}
      className="contents"
      onFiles={(files) => {
        void (async () => {
          for (const file of files) {
            await handlers.onAttach(file);
          }
          setDropped((count) => count + 1);
        })();
      }}
    >
      <DetailFrame
        wide={wide}
        header={<Header data={data} handlers={handlers} />}
        rail={
          <>
            <StatusCard data={data} handlers={handlers} />
            <MoneyCard data={data} handlers={handlers} />
            <DetailsCard data={data} handlers={handlers} />
          </>
        }
        main={
          <>
            {/**
             * ┌───────────────────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ C55 · A LICENCE HAS NO WORK, SO IT DRAWS NEITHER OF THESE.           │
             * └───────────────────────────────────────────────────────────────────────────┘
             *
             * The type exists for revenue that arrives without anybody working, and the schema
             * enforces it — `contract_line_items_licence_has_no_work`. So *Feladatok* offered an
             * assignment UI for rows that cannot exist, and *Erőforrás-allokációk* drew three
             * zeroes; `project-resource-tab.tsx` already excludes licences from planning for the
             * same reason.
             *
             * ⚠️ **Hidden rather than emptied.** An empty section with a *"még nincs ide rendelt
             * feladat"* invites somebody to add one, and the server would refuse them.
             */}
            {!hasWork ? null : (
              <>
                <Tasks data={data} handlers={handlers} compact={!wide} />
                <AllocationsCard data={data} />
              </>
            )}
            {/* 22 Oct 2026 · without the project's money an invoice is nothing but money. */}
            {data.item.moneyVisible ? <InvoicesCard data={data} handlers={handlers} /> : null}
            <ExternalCostsCard data={data} handlers={handlers} />
            <MetafieldsCard
              fields={data.metafields}
              values={data.item.metafields}
              members={data.members}
              canEdit={handlers.canManage}
              onSave={handlers.onMetafields}
            />
            <LineItemAttachments data={data} handlers={handlers} dropped={dropped} />
            <History data={data} />
          </>
        }
      />
    </ScopedDropSurface>
  );
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ PÉNZÜGY — C55. What it was sold for, and what it is costing.      │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * The mockup's own five rows — Elszámolás, Büdzsé, Bevétel, Kiszámlázva, Hátralévő — with the bar
 * under them. Two departures from it, both forced and both recorded:
 *
 * ⚠️ **C57 FILLED IN THE BOTTOM HALF.** *Kiszámlázva* and *Hátralévő* answered zero while there was
 * nowhere for an invoice to live; they are now a real sum and the bar under them is the mockup's
 * own `invPct`, with *Számlázás* beside it.
 *
 * ⚠️⚠️ **AND A MARGIN ROW THE MOCKUP DOES NOT DRAW.** It computes `liMarginPct` and shows it
 * nowhere on this screen. The gap between what a deliverable earns and what it costs is the one
 * figure this card exists to make visible — and we now have both halves honestly, so leaving it
 * out would be copying an omission rather than a design.
 */
function MoneyCard({
  data,
  handlers,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
}) {
  const t = useT();
  const money = data.economics;
  if (money === null) return null;

  const symbol =
    data.currencies.find((one) => one.code === money.currencyCode)?.symbol ??
    money.currencyCode ??
    '';
  const show = (value: string | null): string =>
    value === null || money.currencyCode === null
      ? '–'
      : formatMoney(Number(value), { code: money.currencyCode, symbol });

  /* ⚠️ The card is editable only where the caller may manage contracts — `onMoney` is absent
     otherwise, and a box that accepts typing and then drops it is worse than a figure. */
  const editable = handlers.onMoney !== undefined;
  /* ⚠️ The workspace's base, for the first figure typed onto an unpriced line item: a figure
     with no currency is refused by `0131`, so one has to travel with it (`base-currency.ts`). */
  const defaultCurrency = baseCurrencyOf(data.currencies)?.code ?? 'HUF';

  const budget = money.budget === null ? null : Number(money.budget);
  const cost = Number(money.cost);

  /**
   * ⚠️⚠️ U-5 · **ON A RECURRING CONTRACT THE TYPED FIGURES ARE PER PERIOD.** The boxes edit the
   * stored per-period budget and income, and a line under each says what that comes to over the
   * periods this line item spans — `periods` and the totals are the server's, so the card and the
   * contract's table cannot disagree. Off a recurring contract nothing changes.
   */
  const perPeriod = data.recurring;
  const totalLine = (total: string | null): ReactNode =>
    !perPeriod || total === null ? null : (
      <span className="text-muted-foreground px-2 text-[11px] tabular-nums">
        {t('lineItem.money.totalOver', { count: money.periods, total: show(total) })}
      </span>
    );
  /* U-7 · a licence is income and nothing else, so it draws no budget at all. */
  const showBudget = data.item.type !== 'license';
  /* ⚠️ Against the BUDGET where there is one — *how much of the frame have we spent* — and it is
     the question the bar is read for. A T&M item has no frame, so it has no bar. */
  const spent = budget === null || budget <= 0 ? null : Math.round((cost / budget) * 100);

  /* C57 · ⚠️ A `void` invoice counts for nothing, here as everywhere else in this phase. */
  const live = data.invoices.filter((one) => one.status !== 'void');
  const latest = [...live].sort((a, b) => b.issuedOn.localeCompare(a.issuedOn))[0];
  const billed = billedPct(Number(money.revenue), Number(money.invoiced));

  /**
   * ⚠️⚠️ **EVERY FIGURE ON THE CARD SAYS WHAT IT IS — 14 Oct.** *"Add an info button next to every
   * value, like on the financial dashboard, explaining what each value is."* The dashboard's four
   * parts — name, meaning, formula, how to read it — written for ONE deliverable rather than a
   * scope, and against the arithmetic `economics.service.ts` actually does: cost at the rate card in
   * force on the day, margin against revenue, the remainder floored at zero.
   *
   * ⚠️ **The revenue's explanation follows the TYPE**, because the figure is a different fact on
   * each: a price somebody agreed, hours times a rate, or nothing at all.
   */
  const revenueTip: FinanceTipText =
    data.item.type === 'time_and_material'
      ? {
          name: t('lineItem.money.revenue'),
          what: t('lineItem.money.tip.revenue.whatTm'),
          calc: t('lineItem.money.tip.revenue.calcTm'),
          note: t('lineItem.money.tip.revenue.noteTm'),
        }
      : data.item.type === 'non_billable'
        ? { name: t('lineItem.money.revenue'), what: t('lineItem.money.tip.revenue.whatNone') }
        : {
            name: perPeriod ? t('lineItem.money.revenuePerPeriod') : t('lineItem.money.revenue'),
            what: t('lineItem.money.tip.revenue.what'),
            ...(perPeriod ? { note: t('lineItem.money.tip.perPeriod') } : {}),
          };
  const tips = {
    rate: {
      name: t('lineItem.money.rate'),
      what: t('lineItem.money.tip.rate.what'),
      note: t('lineItem.money.tip.rate.note'),
    },
    budget: {
      name: perPeriod ? t('lineItem.money.budgetPerPeriod') : t('lineItem.money.budget'),
      what: t('lineItem.money.tip.budget.what'),
      note: perPeriod ? t('lineItem.money.tip.perPeriod') : t('lineItem.money.tip.budget.note'),
    },
    currency: {
      name: t('lineItem.money.currency'),
      what: t('lineItem.money.tip.currency.what'),
      note: t('lineItem.money.tip.currency.note'),
    },
    cost: {
      name: t('lineItem.money.cost'),
      what: t('lineItem.money.tip.cost.what'),
      calc: t('lineItem.money.tip.cost.calc'),
      note: t('lineItem.money.tip.cost.note'),
    },
    margin: {
      name: t('lineItem.money.margin'),
      what: t('lineItem.money.tip.margin.what'),
      calc: t('lineItem.money.tip.margin.calc'),
      note: t('lineItem.money.tip.margin.note'),
    },
    /* ⚠️ The dashboard's own formula — the same sum over the same invoices, `void` left out. */
    /* ⚠️ U-6 · on a recurring contract it is the invoiced PERIODS, not invoices of its own. */
    invoiced: perPeriod
      ? {
          name: t('lineItem.money.invoiced'),
          what: t('lineItem.money.tip.invoiced.whatRecurring'),
          calc: t('lineItem.money.tip.invoiced.calcRecurring'),
          note: t('lineItem.money.tip.invoiced.note'),
        }
      : {
          name: t('lineItem.money.invoiced'),
          what: t('lineItem.money.tip.invoiced.what'),
          calc: t('finance.tip.invoiced.calc'),
          note: t('lineItem.money.tip.invoiced.note'),
        },
    remaining: {
      name: t('lineItem.money.remaining'),
      what: t('lineItem.money.tip.remaining.what'),
      calc: t('lineItem.money.tip.remaining.calc'),
      note: t('lineItem.money.tip.remaining.note'),
    },
  } satisfies Record<string, FinanceTipText>;

  return (
    <RailCard
      title={t('lineItem.money')}
      {...(handlers.onMoney === undefined ? {} : { note: t(LINE_ITEM_TYPE_KEY[data.item.type]) })}
    >
      {/**
       * ⚠️⚠️ **WHICH FIGURES A DELIVERABLE CARRIES IS DECIDED BY ITS TYPE, AND THE CARD DRAWS ONLY
       * THOSE.** `fixed` is sold for a price — a budget and a revenue. `time_and_material` is sold
       * by the hour — a rate, and a revenue computed from the hours rather than typed. A
       * `non_billable` deliverable has an internal budget and earns nothing. The mockup's own
       * `liShowBudget` / `liShowRevenue` / `liShowRate` make exactly these three tests, and drawing
       * a disabled Bevétel on a T&M row would invite somebody to type a figure the server drops.
       */}
      {data.item.type === 'time_and_material' ? (
        <RailRow label={t('lineItem.money.rate')} tip={tips.rate}>
          {editable ? (
            <RailMoney
              label={t('lineItem.money.rate')}
              value={money.rate ?? ''}
              suffix={`${symbol} / ${t(
                money.rateUnit === 'day' ? 'settings.rates.unit.day' : 'settings.rates.unit.hour',
              ).toLowerCase()}`}
              onCommit={(rate) => {
                /* ⚠️ The unit travels with the rate — `0131`'s check makes the pair atomic, and an
                   hourly figure stored as a daily one is wrong by a factor of eight. */
                handlers.onMoney?.({
                  rate,
                  rateUnit: rate === null ? null : (money.rateUnit ?? 'hour'),
                  ...(rate === null ? {} : { currencyCode: money.currencyCode ?? defaultCurrency }),
                });
              }}
            />
          ) : (
            <RailValue>{show(money.rate)}</RailValue>
          )}
        </RailRow>
      ) : !showBudget ? null : (
        <RailRow label={tips.budget.name} tip={tips.budget}>
          <span className="flex min-w-0 flex-col">
            {editable ? (
              <RailMoney
                label={tips.budget.name}
                value={money.budgetPerPeriod ?? ''}
                suffix={symbol}
                onCommit={(budget) => {
                  handlers.onMoney?.({
                    budget,
                    ...(budget === null
                      ? {}
                      : { currencyCode: money.currencyCode ?? defaultCurrency }),
                  });
                }}
              />
            ) : (
              <RailValue>{show(money.budgetPerPeriod)}</RailValue>
            )}
            {totalLine(money.budget)}
          </span>
        </RailRow>
      )}

      {data.item.type === 'fixed' || data.item.type === 'license' ? (
        /* ⚠️ U-7 · a licence's income is typed like a fixed item's — it is all a licence has. */
        <RailRow label={revenueTip.name} tip={revenueTip}>
          <span className="flex min-w-0 flex-col">
            {editable ? (
              <RailMoney
                label={revenueTip.name}
                value={money.revenuePerPeriod ?? ''}
                suffix={symbol}
                onCommit={(revenue) => {
                  handlers.onMoney?.({
                    revenue,
                    ...(revenue === null
                      ? {}
                      : { currencyCode: money.currencyCode ?? defaultCurrency }),
                  });
                }}
              />
            ) : (
              <RailValue>{show(money.revenuePerPeriod)}</RailValue>
            )}
            {totalLine(money.revenue)}
          </span>
        </RailRow>
      ) : (
        <RailRow label={t('lineItem.money.revenue')} tip={revenueTip}>
          {/* ⚠️ A T&M deliverable's revenue is COMPUTED from the hours it has taken, so it is a
              figure to read rather than one to type — the rate above is the input. */}
          <RailValue>{show(money.revenue)}</RailValue>
        </RailRow>
      )}

      {/* ⚠️ The currency belongs to the figures, so it sits with them rather than in the details
          card — and it is absent until there is a figure for it to be about. */}
      {/* ⚠️ `revenue` is never null on the wire — a T&M item's is computed and a non-billable
          one's is zero — so the test is over the two figures somebody can actually type. */}
      {editable && (money.budget !== null || money.rate !== null) ? (
        <RailRow label={t('lineItem.money.currency')} tip={tips.currency}>
          <RailPicker
            label={t('lineItem.money.currency')}
            value={money.currencyCode ?? defaultCurrency}
            items={data.currencies.map((one) => ({
              label: `${one.code} · ${one.symbol}`,
              checked: (money.currencyCode ?? defaultCurrency) === one.code,
              onSelect: () => {
                handlers.onMoney?.({ currencyCode: one.code });
              },
            }))}
          />
        </RailRow>
      ) : null}
      <RailRow label={t('lineItem.money.cost')} tip={tips.cost}>
        <RailValue>{show(money.cost)}</RailValue>
      </RailRow>
      <RailRow label={t('lineItem.money.margin')} tip={tips.margin}>
        <RailValue>
          {money.marginPct === null ? (
            '–'
          ) : (
            /* ⚠️ A LOSS is the thing worth seeing at a glance, so the sign gets a colour and the
               healthy case does not — a card where every figure is coloured says nothing. */
            <span className={money.marginPct < 0 ? 'text-destructive font-medium' : ''}>
              {`${String(money.marginPct)}%`}
            </span>
          )}
        </RailValue>
      </RailRow>

      <RailRow label={t('lineItem.money.invoiced')} divided tip={tips.invoiced}>
        <RailValue>{show(money.invoiced)}</RailValue>
      </RailRow>
      <RailRow label={t('lineItem.money.remaining')} tip={tips.remaining}>
        <RailValue>{show(money.remaining)}</RailValue>
      </RailRow>

      {/**
       * ⚠️⚠️ **TWO BARS, AND THEY ANSWER DIFFERENT QUESTIONS.** The one below is *how much of the
       * frame have we spent*; this one is *how much of what we earned have we asked for*. Merging
       * them into a single progress control — which is the tempting simplification — would produce
       * a figure that is neither.
       *
       * ⚠️ Past 100% is the destructive colour and the width still caps: an invoice larger than the
       * revenue is billing ahead of delivery, which is legitimate and worth seeing.
       */}
      {billed === null ? null : (
        <div className="px-1 pt-1">
          <div className="bg-muted h-1.5 overflow-hidden rounded-full">
            <div
              className="h-full"
              style={{
                width: `${String(Math.min(100, billed))}%`,
                background: billed > 100 ? 'var(--destructive)' : 'var(--primary)',
              }}
            />
          </div>
          <p className="text-muted-foreground mt-1 text-[11px]">
            {t('invoice.billedPct', { pct: billed })}
          </p>
        </div>
      )}

      <div className="mt-1.5 flex items-center gap-2 px-1">
        {/* ⚠️ R24 (3 Nov 2026) · *Még nincs számlázva* only when the figure above says 0. A
            recurring contract invoices its PERIODS (U-6), so its line item has no invoices of its
            own while `invoiced` counts the billed periods — the card said both at once. */}
        {latest === undefined ? (
          Number(money.invoiced) > 0 ? null : (
            <span className="text-muted-foreground text-[11.5px]">{t('invoice.none')}</span>
          )
        ) : (
          <button
            type="button"
            className="text-primary flex min-w-0 items-center gap-1 text-[11.5px] hover:underline"
            onClick={() => handlers.onOpenInvoice?.(latest)}
          >
            <Icon name="file-text" size={12} />
            <span className="truncate tabular-nums">{latest.externalId}</span>
            {live.length > 1 ? (
              <span className="text-muted-foreground">{`+${String(live.length - 1)}`}</span>
            ) : null}
          </button>
        )}
        <div className="flex-1" />
        {handlers.onBill === undefined ? null : (
          <Button
            variant="outline"
            size="sm"
            className="h-[26px] gap-1.5 px-2 text-[11px]"
            onClick={handlers.onBill}
          >
            <Icon name="file-text" size={12} />
            {latest === undefined ? t('invoice.bill') : t('invoice.billMore')}
          </Button>
        )}
      </div>

      {spent === null ? null : (
        <div className="px-1 pt-1">
          <div className="bg-muted h-1.5 overflow-hidden rounded-full">
            <div
              className="h-full"
              style={{
                width: `${String(Math.min(100, spent))}%`,
                /* ⚠️ Capped at 100 and the COLOUR says it went over — a bar drawn past its own
                   track is clipped and says nothing. */
                background: spent > 100 ? 'var(--destructive)' : 'var(--primary)',
              }}
            />
          </div>
          <p className="text-muted-foreground mt-1 text-[11px]">
            {t('lineItem.money.spent', { pct: spent })}
          </p>
        </div>
      )}

      {money.unratedMembers === 0 ? null : (
        /**
         * ⚠️⚠️ **THE COST IS UNDERSTATED AND THE CARD HAS TO SAY SO.** Hours logged by somebody
         * with no rate card in force are counted in the hours and priced at nothing — so a reader
         * comparing a healthy margin against a colleague's memory would be reading a number that is
         * missing a person, with the screen looking complete.
         */
        <p className="text-muted-foreground px-1 pt-2 text-[11px]">
          {t('lineItem.money.unrated', { count: money.unratedMembers })}
        </p>
      )}
    </RailCard>
  );
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ ERŐFORRÁS-ALLOKÁCIÓK — C55's three tiles.                            │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * *Betervezett · Rögzített · Maradék terv* — hours rather than money, which is why they sit apart
 * from the card above rather than inside it. The plan and the log are the same question the grid
 * asks; this is that question narrowed to one deliverable.
 */
function AllocationsCard({ data }: { readonly data: LineItemDetailData }) {
  const { fmt } = useDuration();
  const t = useT();
  const money = data.economics;
  if (money === null) return null;

  const planned = Number(money.plannedHours);
  const logged = Number(money.loggedHours);
  const estimate = Number(data.item.estimateHours);

  const tiles = [
    {
      key: 'planned',
      value: fmt(planned),
      /* ⚠️ Against the ESTIMATE, which is the sentence *have we planned more than we said it
         would take* — and the one a planner is checking on this screen. */
      hint:
        estimate > 0
          ? t('lineItem.alloc.ofEstimate', { pct: Math.round((planned / estimate) * 100) })
          : t('lineItem.alloc.noEstimate'),
      over: estimate > 0 && planned > estimate,
    },
    {
      key: 'logged',
      value: fmt(logged),
      hint:
        planned > 0
          ? t('lineItem.alloc.ofPlan', { pct: Math.round((logged / planned) * 100) })
          : t('lineItem.alloc.noPlan'),
      over: false,
    },
    {
      key: 'remaining',
      /* ⚠️ Never below zero: somebody who has logged past the plan has none of it left, which is
         a different problem and one the middle tile's percentage already tells. */
      value: fmt(Math.max(0, planned - logged)),
      hint: t('lineItem.alloc.estimate', { hours: fmt(estimate) }),
      over: false,
    },
  ] as const;

  return (
    <section>
      <SectionHeader title={t('lineItem.alloc')} />
      <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {tiles.map((tile) => (
          <div key={tile.key} className="bg-card rounded-xl border p-3 shadow-sm">
            <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
              {t(`lineItem.alloc.${tile.key}` as 'lineItem.alloc.planned')}
            </div>
            <div
              className={`mt-0.5 text-[19px] font-medium tabular-nums ${
                tile.over ? 'text-destructive' : ''
              }`}
            >
              {tile.value}
            </div>
            <div className="text-muted-foreground mt-0.5 text-[11px]">{tile.hint}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ SZÁMLÁK — C57, on the deliverable they bill.                      │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The header's action is the SAME *Számlázás* as the rail card's**, deliberately: the mockup
 * draws it in both places, and somebody reading the list of what has been billed is exactly the
 * person about to add to it.
 *
 * ⚠️ **A `void` row is still LISTED**, and only excluded from the sum. A sztornó invoice is a thing
 * that happened, and a list that hides it makes a gap in the external numbering look like a bug.
 */
function InvoicesCard({
  data,
  handlers,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
}) {
  const t = useT();
  const invoices = data.invoices;

  const money = (amount: string, code: string): string => {
    const known = data.currencies.find((one) => one.code === code);
    return formatMoney(Number(amount), { code, symbol: known?.symbol ?? code });
  };

  const currencyCode = data.economics?.currencyCode ?? invoices[0]?.currencyCode ?? 'HUF';
  const total = invoices
    .filter((one) => one.status !== 'void')
    .reduce((sum, one) => sum + Number(one.amount), 0);

  return (
    <section>
      <SectionHeader
        title={t('invoice.section')}
        note={
          invoices.length === 0
            ? undefined
            : t('invoice.section.note', {
                count: invoices.length,
                amount: money(total.toFixed(2), currencyCode),
              })
        }
      >
        {handlers.onBill === undefined ? null : (
          <SectionAction
            label={invoices.length === 0 ? t('invoice.bill') : t('invoice.billMore')}
            onClick={handlers.onBill}
          />
        )}
      </SectionHeader>

      {invoices.length === 0 ? (
        <p className="text-muted-foreground bg-card mt-1.5 rounded-xl border p-4 text-center text-[12.5px] shadow-sm">
          {/* ⚠️ U-6 · a recurring contract bills its PERIODS — say where the invoices are. */}
          {data.recurring ? t('invoice.section.byPeriod') : t('invoice.section.emptyLineItem')}
        </p>
      ) : (
        <div className="bg-card mt-1.5 rounded-xl border shadow-sm">
          {[...invoices]
            .sort((a, b) => b.issuedOn.localeCompare(a.issuedOn))
            .map((invoice) => (
              <button
                key={invoice.id}
                type="button"
                className="hover:bg-muted/50 flex w-full items-center gap-3 border-b px-3 py-2 text-left transition-colors last:border-0"
                onClick={() => handlers.onOpenInvoice?.(invoice)}
              >
                <span className="text-muted-foreground w-[72px] shrink-0 text-[11px] tabular-nums">
                  {invoice.identifier}
                </span>
                <span className="min-w-0 flex-1 truncate text-[12px] tabular-nums">
                  {invoice.externalId}
                </span>
                <span className="text-muted-foreground w-[84px] shrink-0 text-[11.5px] tabular-nums">
                  {invoice.issuedOn}
                </span>
                {/* 30 Oct 2026 · fixed cells, so the columns line up row to row (UAT). */}
                <span className="flex w-[92px] shrink-0 justify-end">
                  <Badge variant={invoice.status === 'paid' ? 'secondary' : 'outline'}>
                    {invoiceStatusLabel(t, invoice.status)}
                  </Badge>
                </span>
                <span className="w-[112px] shrink-0 text-right text-[12px] tabular-nums">
                  {money(invoice.amount, invoice.currencyCode)}
                </span>
              </button>
            ))}
        </div>
      )}
    </section>
  );
}

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ KÜLSŐ KÖLTSÉGEK — C56, on the deliverable they were spent on.          │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **An ADD here as well as on the project's tab** (14 Oct 2026, ux-pass-2: invoices and
 * attachments had one and this did not). It opens the same dialog with the link already set to
 * this line item, so there is still one editor and one link picker.
 *
 * ⚠️ **A recurring row shows BOTH figures.** Its amount is what is charged each period and its
 * total is what the run comes to — and the second is the one that moves a margin, so a card showing
 * only the first would understate this deliverable's cost by however many periods it runs for.
 */
function ExternalCostsCard({
  data,
  handlers,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
}) {
  const t = useT();
  const costs = data.externalCosts;

  const money = (amount: string, code: string): string => {
    const known = data.currencies.find((one) => one.code === code);
    return formatMoney(Number(amount), { code, symbol: known?.symbol ?? code });
  };

  /* ⚠️ Converted, for the reason the project tab records: summing two currencies into one figure
     is a number in no unit at all, and one row here is a European supplier in the demo. FX-3 · by
     the server, each charge at its own day's rate (`converted`, in the line item's currency). */
  const currencyCode =
    costs[0]?.converted?.currencyCode ??
    data.economics?.currencyCode ??
    costs[0]?.currencyCode ??
    'HUF';
  const total = costs.reduce((sum, cost) => sum + Number(cost.converted?.total ?? 0), 0);
  /* 22 Oct 2026 · the costs are listed to a project reader; their figures need its money. */
  const showMoney = data.item.moneyVisible;

  return (
    <section>
      <SectionHeader
        title={t('extcost')}
        note={
          costs.length === 0
            ? undefined
            : showMoney
              ? `${t('extcost.count', { count: costs.length })} · ${money(total.toFixed(2), currencyCode)}`
              : t('extcost.count', { count: costs.length })
        }
      >
        {handlers.onNewCost === undefined ? null : (
          <SectionAction label={t('extcost.new')} onClick={handlers.onNewCost} />
        )}
      </SectionHeader>

      {costs.length === 0 ? (
        <p className="text-muted-foreground bg-card mt-1.5 rounded-xl border p-4 text-center text-[12.5px] shadow-sm">
          {t('extcost.empty')}
        </p>
      ) : (
        <div className="bg-card mt-1.5 rounded-xl border shadow-sm">
          {costs.map((cost) => (
            <div key={cost.id} className="flex items-center gap-3 border-b px-3 py-2 last:border-0">
              <span className="text-muted-foreground w-[76px] shrink-0 text-[11.5px] tabular-nums">
                {cost.identifier}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-medium">{cost.name}</span>
                <span className="text-muted-foreground block truncate text-[11px]">
                  {`${cost.incurredOn} · ${cost.ownerName}`}
                </span>
              </span>
              {/* 30 Oct 2026 · both slots drawn even when empty, at fixed widths, so the columns line up
                  row to row (UAT). */}
              <span className="flex w-[160px] shrink-0 justify-end">
                {cost.periods === 1 || cost.total === null || cost.currencyCode === null ? null : (
                  <Badge variant="secondary" className="max-w-full truncate">
                    {t('extcost.periods', {
                      count: cost.periods,
                      total: money(cost.total, cost.currencyCode),
                    })}
                  </Badge>
                )}
              </span>
              <span className="w-[112px] shrink-0 text-right text-[12px] tabular-nums">
                {cost.amount === null || cost.currencyCode === null
                  ? null
                  : money(cost.amount, cost.currencyCode)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ `0116` · THE LINE ITEM'S OWN FOLDER.                                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The contract has had one since 0044 and a line item had nowhere, so an estimate, a scope note or
 * a client's sign-off for ONE item went onto the contract and joined its siblings' documents in a
 * list nobody could tell apart.
 *
 * ⚠️ **`DropZone` rather than a button, and the surface above it as well.** Two ways in, because
 * the drop is the fast one and the picker is the one somebody finds when they are looking.
 */
function LineItemAttachments({
  data,
  handlers,
  dropped,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
  readonly dropped: number;
}) {
  const t = useT();
  const [failure, setFailure] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  return (
    <section>
      {failure === null ? null : (
        <p className="text-destructive mb-1 text-[11.5px]">{t('task.attachment.failed')}</p>
      )}

      {/**
       * ⚠️⚠️ **THE SAME ONE ROW THE TASK GOT — feedback round 4 item 12, everywhere.** This kept
       * its own `SectionHeader` and its own `DropZone`: the dashed box, the sentence of formats and
       * the browse link, above a list that is usually empty. Item 12 replaced that shape on the
       * TASK and left every other subject on the old one, which is how a rule meant to make the
       * product consistent made it less so. Reported 20 Sep.
       *
       * ⚠️ The upload is the same handler; what changed is that the component owns the heading, the
       * button and the drop target, so there is one of each in the product rather than five.
       */}
      <Attachments
        key={`${String(nonce)}-${String(dropped)}`}
        target="line_item"
        targetId={data.item.id}
        canEdit={handlers.canManage}
        heading={t('lineItem.attachments')}
        variant="section"
        emptyNote={t('lineItem.attachments.empty')}
        accept={DOCUMENT_FILES}
        onUpload={async (files) => {
          setFailure(null);
          for (const file of files) {
            const code = await handlers.onAttach(file);
            if (code !== null) {
              setFailure(code);
              return;
            }
          }
          setNonce((count) => count + 1);
        }}
      />
    </section>
  );
}

/**
 * ⚠️ **What a line item's document may be**, matching the leave detail's list. The server decides
 * for real — `sniff.ts` reads the magic number — and these two must not disagree.
 */

/* ── header ──────────────────────────────────────────────────────────────────────────────── */

function Header({
  data,
  handlers,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
}) {
  const t = useT();
  const { item } = data;

  const menu: MenuItem[] = [
    ...(handlers.onExpand === undefined
      ? []
      : [{ label: t('task.mode.full'), icon: 'maximize' as const, onSelect: handlers.onExpand }]),
    {
      label: t('lineItem.openContract'),
      icon: 'file-text',
      onSelect: handlers.onOpenContract,
    },
    ...(handlers.canManage
      ? [
          { type: 'separator' as const },
          item.status === 'closed'
            ? {
                label: t('lineItem.action.reopen'),
                icon: 'circle-plus' as const,
                onSelect: () => {
                  handlers.onPatch({ status: 'open' });
                },
              }
            : {
                label: t('lineItem.action.close'),
                icon: 'check' as const,
                onSelect: () => {
                  handlers.onPatch({ status: 'closed' });
                },
              },
        ]
      : []),
  ];

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {/* ⚠️ **The chip reads `HSO-C01/2`, which IS the address** (P2-9) — so the thing somebody
            copies off the screen is the thing that resolves. It still navigates UP to the contract,
            because the arrow is what the mockup draws and the contract is the useful jump. */}
        <CodeChip
          code={item.identifier}
          icon="arrow-right"
          title={t('lineItem.openContract')}
          onClick={handlers.onOpenContract}
        />
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-[12px] transition-colors"
          onClick={handlers.onOpenProject}
        >
          <span
            className="size-2 rounded-[3px]"
            style={{ background: data.projectColor }}
            aria-hidden
          />
          {data.projectName}
        </button>
        {item.status === 'closed' ? (
          <Badge variant="outline">{t('lineItem.closedBadge')}</Badge>
        ) : null}
        <div className="flex-1" />
        <MoreMenu items={menu} label={t('common.more')} />
      </div>

      <InlineTitle
        value={item.name}
        canEdit={handlers.canManage}
        onSave={(name) => {
          handlers.onPatch({ name });
        }}
      />
    </>
  );
}

/* ── the rail ────────────────────────────────────────────────────────────────────────────── */

const LINE_ITEM_TYPES = ['fixed', 'time_and_material', 'non_billable'] as const;

function StatusCard({
  data,
  handlers,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const { item } = data;
  const estimate = Number(item.estimateHours);
  const tracked = Number(item.trackedHours);
  const pct = estimate > 0 ? Math.round((tracked / estimate) * 100) : 0;

  return (
    <RailCard title={t('contract.column.status')} padded>
      <RailPickerButton
        label={t('contract.column.status')}
        value={t(item.status === 'closed' ? 'lineItem.closed' : 'lineItem.open')}
        color={item.status === 'closed' ? 'var(--muted-foreground)' : 'var(--primary)'}
        disabled={!handlers.canManage}
        items={(['open', 'closed'] as const).map((status) => ({
          label: t(status === 'closed' ? 'lineItem.closed' : 'lineItem.open'),
          dot: status === 'closed' ? 'var(--muted-foreground)' : 'var(--primary)',
          checked: item.status === status,
          onSelect: () => {
            handlers.onPatch({ status });
          },
        }))}
      />

      {/* ⚠️ C55 · A licence earns without hours, so *"Becslés 0:00 · rögzítve 0:00 · 0%"* is a
          progress bar about nothing. It reads as work nobody has started. */}
      {!lineItemHasWork(item.type) ? null : (
        <ProgressBlock
          estimate={fmt(item.estimateHours)}
          tracked={fmt(item.trackedHours)}
          pct={pct}
          over={estimate > 0 && tracked > estimate}
          label={t('contract.column.tracked')}
        />
      )}
    </RailCard>
  );
}

/** The bordered status trigger — the one rail control the mockup gives a border and a dot. */
function RailPickerButton({
  label,
  value,
  color,
  items,
  disabled,
}: {
  readonly label: string;
  readonly value: string;
  readonly color: string;
  readonly items: MenuItem[];
  readonly disabled: boolean;
}) {
  return (
    <div className="mt-1.5">
      <RailPicker
        label={label}
        value={value}
        items={items}
        disabled={disabled}
        before={
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: color }}
            aria-hidden
          />
        }
      />
    </div>
  );
}

function DetailsCard({
  data,
  handlers,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const { item } = data;

  return (
    <RailCard title={t('contract.details')}>
      {handlers.pricing ? (
        <RailRow label={t('contract.column.type')}>
          <RailPicker
            label={t('contract.column.type')}
            disabled={!handlers.canManage}
            value={t(LINE_ITEM_TYPE_KEY[item.type])}
            items={(data.internal ? (['non_billable'] as const) : LINE_ITEM_TYPES).map((type) => ({
              label: t(LINE_ITEM_TYPE_KEY[type]),
              checked: item.type === type,
              onSelect: () => {
                handlers.onPatch({ type });
              },
            }))}
          />
        </RailRow>
      ) : null}

      {item.type === 'time_and_material' ? <PeopleRow data={data} handlers={handlers} /> : null}

      {/**
       * ⚠️⚠️ **HATÓKÖR — AND IT IS ABSENT RATHER THAN DISABLED OFF A RECURRING CONTRACT.**
       * `retainer` work consumes a period's frame and its estimate is what MAKES that frame;
       * `billable` work sits outside it, billed at the rate. A milestone contract has no frame to
       * be inside, so the control would be a choice between two words that mean the same thing
       * there — `liScopeOn` in the mockup makes exactly this test.
       */}
      {!data.recurring || data.economics === null ? null : (
        <RailRow label={t('lineItem.scope')}>
          <RailPicker
            label={t('lineItem.scope')}
            disabled={handlers.onMoney === undefined}
            value={t(
              data.economics.scope === 'billable'
                ? 'lineItem.scope.billable'
                : 'lineItem.scope.retainer',
            )}
            items={(['retainer', 'billable'] as const).map((scope) => ({
              label: t(
                scope === 'billable' ? 'lineItem.scope.billable' : 'lineItem.scope.retainer',
              ),
              description: t(
                scope === 'billable'
                  ? 'lineItem.scope.billableNote'
                  : 'lineItem.scope.retainerNote',
              ),
              checked: data.economics?.scope === scope,
              onSelect: () => {
                handlers.onMoney?.({ scope });
              },
            }))}
          />
        </RailRow>
      )}

      <RailRow label={t('contract.column.project')}>
        <RailLink
          value={data.projectName}
          title={t('contract.action.openProject')}
          onClick={handlers.onOpenProject}
          before={
            <span
              className="size-2 shrink-0 rounded-[3px]"
              style={{ background: data.projectColor }}
              aria-hidden
            />
          }
        />
      </RailRow>

      <RailRow label={t('title.contracts.one')}>
        <RailLink
          value={data.contractName}
          title={t('lineItem.openContract')}
          onClick={handlers.onOpenContract}
        />
      </RailRow>

      <RailRow label={t('contract.column.startsOn')}>
        <RailDate
          label={t('contract.column.startsOn')}
          disabled={!handlers.canManage}
          value={item.startsOn ?? ''}
          onChange={(startsOn) => {
            handlers.onPatch({ startsOn: startsOn === '' ? null : startsOn });
          }}
        />
      </RailRow>

      <RailRow label={t('contract.column.endsOn')}>
        <RailDate
          label={t('contract.column.endsOn')}
          disabled={!handlers.canManage}
          value={item.endsOn ?? ''}
          onChange={(endsOn) => {
            handlers.onPatch({ endsOn: endsOn === '' ? null : endsOn });
          }}
        />
      </RailRow>

      {/**
       * ⚠️⚠️ **C55 · THE THREE WORK ROWS, AND A LICENCE DRAWS NONE OF THEM.** An estimate, a task
       * count and an assignment rule are all questions about work, and a licence is the type that
       * has none: the server refuses hours on it outright. U-7 took its quantity × unit price
       * away too, so its terms are its income, on the money card.
       */}
      {lineItemHasWork(item.type) ? (
        <>
          <RailRow label={t('contract.column.estimate')} divided>
            {handlers.canManage ? (
              <EstimateField item={item} handlers={handlers} />
            ) : (
              <RailValue>{fmt(item.estimateHours)}</RailValue>
            )}
          </RailRow>

          <RailRow label={t('contract.tasks')}>
            {/* 31 Oct 2026 · the server's count — the page holds twenty. */}
            <RailValue>{data.item.taskCount}</RailValue>
          </RailRow>

          <RailRow label={t('lineItem.assignment')}>
            <RailValue mono={false}>
              {t(item.assignment === 'rule' ? 'lineItem.byRule' : 'lineItem.byHand')}
            </RailValue>
          </RailRow>
        </>
      ) : null}
    </RailCard>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ OS-1 · whose hours a time-and-material line item counts.                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Time and material only, and absent rather than disabled elsewhere** — the server refuses
 * people on any other type (`line_item_people_need_tm`), so a control there would be a choice
 * between words that mean nothing. Nobody named reads *Bárki*: the ordinary T&M, where it does not
 * matter who logs.
 *
 * ⚠️ **A checkbox menu over the rail's own picker**, one person per click — each toggle is a
 * PATCH of the WHOLE list, like every other rail control saves on the pick. Offered: project
 * members whose window meets the line item's (P-1, as the server asks it), and anybody already
 * named, so a person who has since left can still be taken off.
 */
function PeopleRow({
  data,
  handlers,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
}) {
  const t = useT();
  const { item } = data;
  const named = item.people.map((person) => person.memberId);
  const meets = (member: ProjectMember): boolean =>
    (item.endsOn === null || member.validFrom <= item.endsOn) &&
    (item.startsOn === null || member.validUntil === null || member.validUntil >= item.startsOn);
  const offered = new Map<string, string>(item.people.map((one) => [one.memberId, one.name]));
  /* R1 · 3 Nov 2026 · a CLOSED line item names nobody new — the people already on it can still be
     taken off. R2 · and a deactivated person is never offered (`member_not_active`). */
  const closed = item.status === 'closed';
  for (const member of data.projectMembers) {
    if (
      !closed &&
      meets(member) &&
      member.memberStatus === 'active' &&
      !offered.has(member.memberId)
    ) {
      offered.set(member.memberId, member.displayName);
    }
  }
  const choices = [...offered].sort(([, a], [, b]) => a.localeCompare(b));

  return (
    <RailRow
      label={t('lineItem.people')}
      tip={{
        name: t('lineItem.people'),
        what: t('lineItem.people.tip.what'),
        note: t('lineItem.people.tip.note'),
      }}
    >
      <RailPicker
        label={t('lineItem.people')}
        disabled={!handlers.canManage}
        value={
          item.people.length === 0
            ? t('lineItem.people.anyone')
            : item.people.map((person) => person.name).join(', ')
        }
        searchPlaceholder={t('lineItem.people.search')}
        items={choices.map(([memberId, name]) => ({
          label: name,
          checked: named.includes(memberId),
          onSelect: () => {
            handlers.onPatch({
              people: named.includes(memberId)
                ? named.filter((one) => one !== memberId)
                : [...named, memberId],
            });
          },
        }))}
      />
    </RailRow>
  );
}

/**
 * ⚠️ **Held as a draft and committed on Enter or blur**, because an hours field re-parsed on every
 * keystroke cannot be typed into: `4` is a valid number on the way to `40`, and sending it makes the
 * estimate flicker to four hours while somebody is still typing.
 *
 * ⚠️ **Shown FORMATTED and parsed by the shared helper** — the field reads `40:00` and accepts
 * `40`, `40h`, `40:00` and `40,0`, like every other hour field in the product (CLAUDE.md §3). The
 * first version showed the raw `40.00` from the wire, which is the column, not the value.
 */
function EstimateField({
  item,
  handlers,
}: {
  readonly item: LineItem;
  readonly handlers: LineItemDetailHandlers;
}) {
  /* The draft is bare (`1,5`, not `1,5 nap`) so it re-parses in the unit its picker names (DU-2). */
  const [unit, setUnit] = useState<DurationUnit | undefined>(undefined);
  const { fmtNum, parse, retype, unit: shownIn } = useDurationIn(unit);
  const t = useT();
  const [draft, setDraft] = useState(() => fmtNum(item.estimateHours));

  const commit = (): void => {
    const next = parse(draft);
    if (next === null) {
      setDraft(fmtNum(item.estimateHours));
      return;
    }
    if (next !== Number(item.estimateHours)) handlers.onPatch({ estimateHours: next });
  };

  return (
    <div
      className="flex items-center gap-1"
      onBlur={(event) => {
        /* Tabbing from the box to its unit picker is not leaving the field. */
        if (event.currentTarget.contains(event.relatedTarget)) return;
        commit();
      }}
    >
      <TextInput
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
        }}
        aria-label={t('contract.column.estimate')}
        /* 5 Nov 2026 · the rail's own padding and size, so its figure lines up with the rows. */
        className="h-[30px] border-transparent bg-transparent px-2 text-[12.5px] tabular-nums shadow-none"
      />
      <DurationUnitPicker
        unit={shownIn}
        onChange={(next) => {
          setDraft(retype(draft, next, item.estimateHours));
          setUnit(next);
        }}
      />
    </div>
  );
}

/* ── the tasks ───────────────────────────────────────────────────────────────────────────── */

/**
 * The tasks whose hours belong to this line item.
 *
 * ⚠️ **The whole block is ONE bordered card** — toolbar, assign row, header, rows and pager — which
 * is the mockup's shape and was five loose pieces in the first version. The table and its pager are
 * `AssignedTasks` below — the product's own list, because a rule-driven line item on a busy project
 * can hold hundreds.
 */
function Tasks({
  data,
  handlers,
  compact,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
  /** 29 Oct 2026 · in the drawer — the list's toolbar takes its narrow form. */
  readonly compact: boolean;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const [picking, setPicking] = useState(false);
  /**
   * 30 Oct 2026 · a task picked from ANOTHER line item, waiting for its move to be confirmed. The
   * move takes its hours with it — they bill wherever the task is — so it is said before it happens.
   */
  const [moving, setMoving] = useState<Task | null>(null);
  /* 31 Oct 2026 · the picker searches the project on the server — `use-line-item-tasks.ts`. */
  const query = data.taskList.pickerQuery;
  const setQuery = data.taskList.setPickerQuery;
  const matches = data.taskList.pickerMatches;

  /**
   * ⚠️ **Resolved by id, like the exclusions** — a task this rule wanted and did not get is by
   * definition on another line item, so it is not on this one's list.
   */
  const overlapTasks = useMemo(
    () =>
      data.item.overlapTaskIds
        .map((id) => data.taskList.linked.get(id))
        .filter((task): task is Task => task !== undefined),
    [data.item.overlapTaskIds, data.taskList.linked],
  );

  /**
   * ⚠️⚠️ 29 Oct 2026 · **A WRITE THAT MOVES TASKS PULSES THE LIST UNTIL IT LANDS.** A rule, the mode,
   * the target, an assignment or an exclusion re-evaluates the project server-side and the list is
   * read again after it; the count of writes still in flight, with the route's own re-read, is what
   * `isRefreshing` shows. Without it a change looked like nothing happening for a second or two.
   */
  const [saving, setSaving] = useState(0);
  const track = (result: unknown): void => {
    if (!(result instanceof Promise)) return;
    setSaving((count) => count + 1);
    void result.finally(() => {
      setSaving((count) => count - 1);
    });
  };
  const tracked: LineItemDetailHandlers = {
    ...handlers,
    onSyncSettings: (patch) => {
      const result = handlers.onSyncSettings(patch);
      track(result);
      return result;
    },
    onAssign: (task, itemId) => {
      track(handlers.onAssign(task, itemId));
    },
    onExclude: (taskId, excluded) => {
      track(handlers.onExclude(taskId, excluded));
    },
  };
  const refreshing = saving > 0 || data.taskList.loading;
  const canAssign = handlers.canManage && data.item.status !== 'closed';
  /* R1 · 3 Nov 2026 · a closed line item's rule, target and switch to rule mode are read-only:
     each can bring work onto it, and the server refuses them with `line_item_closed`. */
  const canConfigure = canAssign;

  const target = data.item.target;
  const byRule = data.item.assignment === 'rule';

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ 5 Nov 2026 · THE SETTINGS ARE ON THE PAGE, ABOVE THE LIST.            │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * The product owner: the panel moved out of its popover into a card of its own under
   * *Feladatok*, over the list — and the pill line that summarised it, and the *Szerkesztés* button
   * that opened it, went. The card is `LineItemSyncCard`; *Szinkronizálás most* and the counts sit
   * in its footer, beside *Mégsem* and *Mentés*.
   */
  return (
    <section>
      <SectionHeader title={t('lineItem.tasks')} />

      <LineItemSyncCard
        target={target}
        assignment={data.item.assignment}
        rule={data.item.rule}
        options={data.targetOptions}
        ruleOptions={data.ruleOptions}
        canManage={handlers.canManage}
        canConfigure={canConfigure}
        onSave={tracked.onSyncSettings}
        footer={
          <>
            {target !== null && handlers.canManage ? (
              <Button
                variant="outline"
                size="sm"
                className="h-[26px] px-2.5 text-[12px]"
                onClick={handlers.onSync}
              >
                <Icon name="refresh-cw" size={13} />
                <span className="phone:hidden">{t('lineItem.target.syncNow')}</span>
                <span className="hidden phone:inline">{t('lineItem.target.syncShort')}</span>
              </Button>
            ) : null}
            {/* ⚠️ 21 Oct 2026 · **THE LINE ITEM'S OWN COUNTS, NOT THE PAGE'S.** The list holds one
                page — counting it sent the manual count negative. `taskCount` is the server's. */}
            <span className="text-muted-foreground text-[12px] tabular-nums">
              {byRule
                ? t('lineItem.assignedNote.rule', {
                    rule: data.item.ruleTaskCount,
                    manual: Math.max(0, data.item.taskCount - data.item.ruleTaskCount),
                    excluded: data.item.excludedTaskIds.length,
                  })
                : t('lineItem.assignedNote', { count: data.item.taskCount })}
            </span>
          </>
        }
      />

      <div className="mt-3 overflow-hidden rounded-lg border">
        {/* ⚠️⚠️ **P2-2's OVERLAP, SHOWN HERE BECAUSE THERE IS NOWHERE ELSE.** The line item whose
            claim LOST is the only screen that can say so — a rule that quietly moves which
            contract a task's hours belong to is an invoice error. One line under the summary. */}
        {overlapTasks.length === 0 ? null : (
          <p className="flex items-start gap-2 border-b px-3 py-2 text-[11.5px] leading-relaxed text-amber-700 dark:text-amber-400">
            <Icon name="triangle-alert" size={13} className="mt-0.5 shrink-0" />
            <span className="min-w-0">
              {t('lineItem.overlap', { count: overlapTasks.length })}
              {overlapTasks.map((task, index) => (
                <span key={task.id}>
                  {index === 0 ? ' ' : ', '}
                  <button
                    type="button"
                    className="underline-offset-2 hover:underline"
                    onClick={() => {
                      handlers.onOpenTask(task);
                    }}
                  >
                    {task.key}
                  </button>
                </span>
              ))}
            </span>
          </p>
        )}

        {/* ⚠️ Inline above the list that it adds to — `task-picker.tsx`'s rule: a modal is the wrong
          weight for a choice you make three of in a row. Opened from the list's own toolbar. */}
        {picking ? (
          <div className="bg-muted/30 flex flex-col gap-2 border-b p-3">
            <TextInput
              autoFocus
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  /* Handled — the modal around it stays open (`useModalSurface`). */
                  event.preventDefault();
                  setPicking(false);
                }
              }}
              placeholder={t('lineItem.assignSearch')}
              aria-label={t('lineItem.assignSearch')}
            />
            <div className="max-h-[240px] overflow-y-auto">
              {matches.length === 0 ? (
                <p className="text-muted-foreground px-2 py-4 text-center text-[12px]">
                  {t('task.picker.empty')}
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {matches.map((task) => (
                    <li key={task.id}>
                      <button
                        type="button"
                        className="hover:bg-accent/50 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left"
                        onClick={() => {
                          setPicking(false);
                          setQuery('');
                          if (task.lineItemId !== null) {
                            setMoving(task);
                            return;
                          }
                          tracked.onAssign(task, data.item.id);
                        }}
                      >
                        <span
                          className="size-2 shrink-0 rounded-[3px]"
                          style={{ background: task.statusColor }}
                          aria-hidden
                        />
                        <span className="text-muted-foreground w-[64px] shrink-0 truncate text-[11.5px] tabular-nums">
                          {task.key}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
                          {task.title}
                        </span>
                        {/* 30 Oct 2026 · where it is now — picking it moves it from there. */}
                        {task.lineItemId === null ? null : (
                          <span className="text-muted-foreground max-w-[45%] shrink-0 truncate text-[11.5px]">
                            {t('lineItem.move.currentlyOn', {
                              name: task.lineItemName ?? t('lineItem.shared.unknown'),
                            })}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}

        {/* ⚠️⚠️ **THE PRODUCT'S LIST, NOT A FLEX TABLE OF ITS OWN — ux-pass-2, 14 Oct.** In the same
          card as the summary, with *Feladat hozzárendelése* in its toolbar.
          ⚠️ IP-3 · a closed line item takes no new task from a person (`line_item_closed`). */}
        <AssignedTasks
          data={data}
          handlers={tracked}
          refreshing={refreshing}
          compact={compact}
          {...(canAssign
            ? {
                onAssignClick: () => {
                  setPicking((open) => !open);
                  setQuery('');
                },
              }
            : handlers.canManage
              ? /* 30 Oct 2026 · closed: drawn and disabled with the reason, not hidden. */
                { assignDisabledReason: t('lineItem.assignClosed') }
              : {})}
        />
      </div>

      {moving === null ? null : (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setMoving(null);
          }}
          title={t('lineItem.move.title')}
          closeLabel={t('common.close')}
          width={460}
        >
          <div className="flex flex-col gap-3">
            <p className="text-[13px] leading-relaxed">
              {t('lineItem.move.body', {
                task: `${moving.key} · ${moving.title}`,
                hours: fmt(Number(moving.trackedHours)),
                from: moving.lineItemName ?? t('lineItem.shared.unknown'),
                to: data.item.name,
              })}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setMoving(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  const task = moving;
                  setMoving(null);
                  tracked.onAssign(task, data.item.id);
                }}
              >
                {t('lineItem.move.confirm')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
}

/**
 * The tasks on this line item, as `DataList` — search, filters, multi-column sort and the settled
 * `[<] [selector] [>] [total]` pager.
 *
 * ⚠️⚠️ **31 Oct 2026 · PAGED, FILTERED AND SORTED ON THE SERVER** — `use-line-item-tasks.ts`. It
 * filtered and sorted in memory over every task of the project the route held. The filters' options
 * are the workspace's statuses and the project's members now — a page cannot say which of them the
 * whole line item holds — and the footer's hours are the server's sum over the filtered set.
 *
 * ⚠️⚠️ **A RULE-HELD ROW IS EXCLUDED, NOT UNASSIGNED.** Clearing its line item lasts until the next
 * evaluation, which puts it straight back — so the row menu's one entry records a decision the
 * engine reads on a rule row, and clears the link on a manual one. The label says which.
 *
 * ⚠️ **No selection column**: there is no bulk action on a line item's tasks, and a checkbox per
 * row that feeds nothing is `data-list.tsx`'s own reason for `selectable`.
 */
function AssignedTasks({
  data,
  handlers,
  refreshing,
  compact,
  onAssignClick,
  assignDisabledReason,
}: {
  readonly data: LineItemDetailData;
  readonly handlers: LineItemDetailHandlers;
  /** A write that moves tasks is in flight, or the list is being read again. */
  readonly refreshing: boolean;
  /**
   * 29 Oct 2026 · in the drawer: no column chooser, and the filter and *Feladat hozzárendelése* as
   * icons beside the search — *"the search is too small because of the column setting"*.
   */
  readonly compact: boolean;
  /** Opens the picker above the list. Absent for somebody who may not assign, or a closed item. */
  readonly onAssignClick?: () => void;
  /** 30 Oct 2026 · why the button is drawn disabled — a closed line item. */
  readonly assignDisabledReason?: string;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const list = data.taskList;

  /* One request per distinct picture for the whole list — see `use-avatar.ts`. */
  const avatars = useAvatars(list.rows.map((task) => task.assigneeAvatarUrl));

  const statusOptions = useMemo(
    () => data.ruleOptions.statuses.map((status) => ({ value: status.id, label: status.name })),
    [data.ruleOptions.statuses],
  );
  const assigneeOptions = useMemo(
    () =>
      data.projectMembers
        .map((member) => ({ value: member.memberId, label: member.displayName }))
        .filter((one, index, all) => all.findIndex((other) => other.value === one.value) === index)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data.projectMembers],
  );

  /* IP-3 · the other line items that want each task this one holds. */
  const others = new Map<string, string[]>();
  for (const pair of data.item.sharedWith) {
    others.set(pair.taskId, [...(others.get(pair.taskId) ?? []), pair.lineItemId]);
  }
  const nameOf = (lineItemId: string): string =>
    data.lineItemNames.get(lineItemId) ?? t('lineItem.shared.unknown');

  const excluded = new Set(data.item.excludedTaskIds);
  const rows: DataListRow[] = list.rows.map((task) => ({
    id: task.id,
    cells: {
      key: {
        text: task.key,
        mono: true,
        muted: true,
        ...(others.has(task.id)
          ? {
              warning: t('lineItem.shared.tip', {
                names: (others.get(task.id) ?? []).map(nameOf).join(', '),
              }),
            }
          : {}),
      },
      title: { text: task.title },
      status: { dot: task.statusColor, text: task.statusName },
      /* ⚠️ An em-dash for nobody — `state-rules.md` §5's absent value. */
      assignee:
        task.assigneeName === null
          ? { text: '–', muted: true }
          : {
              text: task.assigneeName,
              avatar: {
                initials: initialsOf(task.assigneeName),
                ...(task.assigneeAvatarSeed === null ? {} : { seed: task.assigneeAvatarSeed }),
                title: task.assigneeName,
                src: avatars.get(task.assigneeAvatarUrl ?? ''),
              },
            },
      /* 5 Nov 2026 · a thrown-back task says so, whatever line item holds it now. */
      source: excluded.has(task.id)
        ? { badge: { label: t('rule.excludedLabel'), variant: 'outline' as const } }
        : {
            badge: {
              label: t(task.lineItemSource === 'rule' ? 'lineItem.byRule' : 'lineItem.byHand'),
              variant:
                task.lineItemSource === 'rule' ? ('secondary' as const) : ('outline' as const),
            },
          },
      hours: { text: fmt(task.trackedHours), mono: true, align: 'right' as const },
    },
  }));

  const filtered =
    list.query !== '' || list.status !== '' || list.assignee !== '' || list.source !== '';

  const rowMenu = (id: string): MenuItem[] => {
    const task = list.rows.find((one) => one.id === id);
    if (task === undefined) return [];
    /* 5 Nov 2026 · an excluded row's one entry takes the exclusion off — the rule claims it again. */
    if (excluded.has(task.id)) {
      return handlers.canManage
        ? [
            {
              label: t('rule.restore'),
              icon: 'undo',
              onSelect: () => {
                handlers.onExclude(task.id, false);
              },
            },
          ]
        : [];
    }
    const rule = task.lineItemSource === 'rule';
    /* IP-3 · a task another line item also wants opens that one from here. */
    const open: MenuItem[] = (others.get(task.id) ?? []).map((lineItemId) => ({
      label: t('lineItem.shared.open', { name: nameOf(lineItemId) }),
      icon: 'triangle-alert',
      onSelect: () => {
        handlers.onOpenLineItem(lineItemId);
      },
    }));
    if (!handlers.canManage) return open;
    return [
      ...open,
      {
        /* 5 Nov 2026 · a word, not a sentence naming the key — the row already says which task. */
        label: t(rule ? 'lineItem.exclude' : 'lineItem.unassign'),
        icon: 'x',
        onSelect: () => {
          if (rule) handlers.onExclude(task.id, true);
          else handlers.onAssign(task, null);
        },
      },
    ];
  };

  return (
    <DataList
      columnsKey="line-item-tasks"
      compactToolbar={compact}
      /* 29 Oct 2026 · a list inside a detail: the table on a desktop, in every view, and no toggle. */
      tableOnDesktop
      card={{
        title: 'title',
        context: 'key',
        defaults: { badge: 'status', avatar: 'assignee', details: ['source', 'hours'] },
      }}
      selectable={false}
      columns={[
        { key: 'key', label: t('lineItem.taskKey'), width: 96, sortKey: 'key' },
        { key: 'title', label: t('project.tasks.column.title'), sortKey: 'title' },
        { key: 'status', label: t('contract.column.status'), width: 130, sortKey: 'status' },
        {
          key: 'assignee',
          label: t('project.tasks.column.assignee'),
          width: 160,
          sortKey: 'assignee',
        },
        /* ⚠️ The server's sort keys (`TASK_SORT`) — the column key and the sort key differ here. */
        {
          key: 'source',
          label: t('lineItem.source'),
          width: 96,
          sortKey: 'line_item_source',
        },
        {
          key: 'hours',
          label: t('common.hours'),
          width: 72,
          align: 'right',
          sortKey: 'tracked_hours',
        },
      ]}
      rows={rows}
      page={list.page}
      sort={list.sort}
      onSortChange={list.setSort}
      search={list.query}
      onSearchChange={list.setQuery}
      filters={[
        {
          key: 'status',
          label: t('contract.column.status'),
          /* 3 Nov 2026 · comma lists the server ORs (`list-filter.ts`). */
          kind: 'multi' as const,
          value: list.status,
          onChange: list.setStatus,
          options: statusOptions,
        },
        {
          key: 'assignee',
          label: t('project.tasks.column.assignee'),
          kind: 'multi' as const,
          value: list.assignee,
          onChange: list.setAssignee,
          /* 3 Nov 2026 · *Nincs felelős* is an ordinary option of the multi (R-62, amended). */
          options: [...assigneeOptions, { value: NOBODY, label: t('lineItem.tasks.unassigned') }],
        },
        {
          key: 'source',
          label: t('lineItem.source'),
          value: list.source,
          onChange: list.setSource,
          options: [
            { value: 'manual', label: t('lineItem.byHand') },
            { value: 'rule', label: t('lineItem.byRule') },
            /* 5 Nov 2026 · what the rule was told to leave alone — `use-line-item-tasks.ts`. */
            { value: EXCLUDED, label: t('rule.excludedLabel') },
          ],
        },
      ]}
      onRowClick={(id) => {
        const task = list.rows.find((one) => one.id === id);
        if (task !== undefined) handlers.onOpenTask(task);
      }}
      {...(handlers.canManage || data.item.sharedWith.length > 0 ? { rowMenu } : {})}
      /* ⚠️ `state-rules.md` §4: nothing here yet and the filters hid everything are two sentences. */
      empty={
        filtered
          ? { icon: 'search', title: t('lineItem.tasks.emptyFiltered'), body: '' }
          : { icon: 'list', title: t('lineItem.tasks.empty'), body: '' }
      }
      /* ⚠️ The FILTERED set's count and hours, from the server — never a sum of one page. */
      footerNote={t('lineItem.tasks.total', {
        count: list.totals?.count ?? list.page.total ?? 0,
        hours: fmt(list.totals?.trackedHours ?? '0'),
      })}
      labels={{ ...listLabels(t), search: t('lineItem.tasks.search') }}
      isRefreshing={refreshing}
      {...(onAssignClick !== undefined
        ? {
            actions: [
              {
                label: t('lineItem.assign'),
                icon: 'plus' as const,
                variant: 'outline' as const,
                onSelect: onAssignClick,
              },
            ],
          }
        : assignDisabledReason !== undefined
          ? {
              actions: [
                {
                  label: t('lineItem.assign'),
                  icon: 'plus' as const,
                  variant: 'outline' as const,
                  disabledReason: assignDisabledReason,
                },
              ],
            }
          : {})}
    />
  );
}

function History({ data }: { readonly data: LineItemDetailData }) {
  /* ux-pass-2 · the one history (`screens/history/`): searchable, filterable, 25 a page, closed
     until somebody opens it. It replaced a list of sentences over unpaged audit rows. */
  return <CollapsibleHistory type="line_item" id={data.item.id} />;
}
